import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FeedbackOverview } from "@/components/FeedbackOverview";
import { MembersAdmin } from "@/components/MembersAdmin";
import { BriefAdmin } from "@/components/Brief";
import { BookMarked } from "lucide-react";
import {
  Search,
  Database,
  FileText,
  GitCompare,
  Loader2,
  ShieldCheck,
  Activity,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Upload,
  BookOpen,
  Trophy, MessageSquareWarning, Users } from "lucide-react";
import { useNav, VIEWS } from "@/lib/nav";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
}

export function AdminPage() {
  return (
    <div className="grid gap-3" data-testid="admin-page">
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-2" data-testid="admin-tabs">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">
            <Activity className="w-3.5 h-3.5 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="models" data-testid="admin-tab-models">
            <Database className="w-3.5 h-3.5 mr-1.5" /> Models DB
          </TabsTrigger>
          <TabsTrigger value="diff" data-testid="admin-tab-diff">
            <GitCompare className="w-3.5 h-3.5 mr-1.5" /> Snapshot diff
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="admin-tab-members">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Members
          </TabsTrigger>
          <TabsTrigger value="brief" data-testid="admin-tab-brief">
            <BookMarked className="w-3.5 h-3.5 mr-1.5" /> Rules brief
          </TabsTrigger>
          <TabsTrigger value="feedback" data-testid="admin-tab-feedback">
            <MessageSquareWarning className="w-3.5 h-3.5 mr-1.5" /> Callout feedback
          </TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><AdminOverview /></TabsContent>
        <TabsContent value="models"><AdminModels /></TabsContent>
        <TabsContent value="diff"><AdminDiff /></TabsContent>
        <TabsContent value="feedback"><FeedbackOverview /></TabsContent>
        <TabsContent value="members"><MembersAdmin /></TabsContent>
        <TabsContent value="brief"><BriefAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}

function AdminOverview() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = () => {
    setLoading(true);
    api.adminInfo().then((d) => setInfo(d)).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);
  if (loading) return <Skeleton className="h-64 w-full" />;
  if (!info) return <p>Failed to load admin info.</p>;

  const snap = info.snapshot.current;
  const pdf = info.rulebook_pdf;

  return (
    <div className="grid gap-3" data-testid="admin-overview">
      <SyncRunner onComplete={reload} />
      <RulebookUploader onComplete={reload} />
      <SteamrollerUploader onComplete={reload} />
      <div className="grid md:grid-cols-3 gap-3">
        <Card data-testid="admin-sync-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <h3 className="font-semibold">Current snapshot</h3>
              {snap && <Badge variant="outline" className="ml-auto text-emerald-400 border-emerald-700/60">live</Badge>}
            </div>
            {snap ? (
              <dl className="text-sm space-y-1">
                <Row label="Version" value={<code className="text-xs">{snap.version_id}</code>} />
                <Row label="Synced at" value={fmtDate(snap.synced_at)} />
                <Row label="Data source" value={snap.data_source || "—"} />
                <Row label="Bundles" value={snap.bundle_count} />
                <Row label="Size" value={fmtBytes(snap.size_bytes)} />
                <Row label="SHA-256" value={<code className="text-[10px] text-muted-foreground">{(snap.sha256 || "").slice(0, 16)}…</code>} />
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground italic">No snapshot loaded.</p>
            )}
          </CardContent>
        </Card>

        <PdfStatusCard pdf={pdf} title="Rulebook PDF" testId="admin-pdf-card" />
        <PdfStatusCard pdf={info.steamroller_pdf} title="Steamroller PDF" testId="admin-steamroller-pdf-card" />
      </div>

      <Card data-testid="admin-counts-card">
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Database className="w-4 h-4 text-primary" /> Data counts
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Cards (live)", info.counts.cards_live, `${info.counts.cards} total`],
              ["Canonical rules", info.counts.rules_canonical, `${info.counts.rules_raw_abilities} raw`],
              ["Duplicates collapsed", info.counts.duplicates_collapsed, "merged into canonical"],
              ["Rulebook sections", info.counts.rulebook_sections, `${info.counts.rulebook_keywords} keywords`],
            ].map(([label, value, sub]) => (
              <div key={label} className="p-3 rounded border border-border bg-background/40">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-2xl font-semibold tabular-nums">{value.toLocaleString()}</p>
                <p className="text-[11px] text-muted-foreground">{sub}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="admin-history-card">
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-amber-400" /> Snapshot history
            <Badge variant="outline" className="ml-1">{info.snapshot.total_count}</Badge>
          </h3>
          <ScrollArea className="max-h-72">
            <div className="space-y-1.5 pr-2">
              {info.snapshot.history.map((s, i) => (
                <div
                  key={s.version_id}
                  className="p-2 rounded border border-border text-sm flex items-center gap-3"
                  data-testid={`snapshot-row-${s.version_id}`}
                >
                  {i === 0 && <Badge variant="outline" className="text-emerald-400 border-emerald-700/60">current</Badge>}
                  <code className="text-xs">{s.version_id}</code>
                  <span className="text-xs text-muted-foreground">{fmtDate(s.synced_at)}</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {s.counts?.cards ?? "?"} cards · {fmtBytes(s.size_bytes)}
                  </span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}

function AdminModels() {
  const { navigate } = useNav();
  const [q, setQ] = useState("");
  const [faction, setFaction] = useState("");
  const [liveOnly, setLiveOnly] = useState(false);
  const [data, setData] = useState({ total: 0, items: [], factions: [] });
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const LIMIT = 100;

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.adminModels({ q, faction, live_only: liveOnly, limit: LIMIT, offset })
        .then((d) => setData(d))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, faction, liveOnly, offset]);

  useEffect(() => { setOffset(0); }, [q, faction, liveOnly]);

  return (
    <div className="grid gap-3" data-testid="admin-models-panel">
      <Card>
        <CardContent className="p-4 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search model name, faction, type…"
                className="pl-9"
                data-testid="admin-models-search"
              />
            </div>
            <select
              value={faction}
              onChange={(e) => setFaction(e.target.value)}
              className="bg-background border border-border rounded px-3 py-1.5 text-sm"
              data-testid="admin-models-faction"
            >
              <option value="">All factions</option>
              {data.factions.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={liveOnly}
                onChange={(e) => setLiveOnly(e.target.checked)}
                data-testid="admin-models-live-only"
              />
              Live only
            </label>
            <span className="text-xs text-muted-foreground ml-auto" data-testid="admin-models-count">
              {loading ? "…" : `${data.total.toLocaleString()} models`}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <ScrollArea className="max-h-[65vh]">
            <table className="w-full text-sm" data-testid="admin-models-table">
              <thead className="text-xs text-muted-foreground border-b border-border sticky top-0 bg-card">
                <tr>
                  <th className="text-left p-2 font-medium">Name</th>
                  <th className="text-left p-2 font-medium">Faction</th>
                  <th className="text-left p-2 font-medium">Type</th>
                  <th className="text-right p-2 font-medium">Rules</th>
                  <th className="text-center p-2 font-medium">Live</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
                  </td></tr>
                )}
                {!loading && data.items.map((m) => (
                  <tr
                    key={m.id}
                    className="border-b border-border/40 hover:bg-card/70 cursor-pointer"
                    onClick={() => navigate(VIEWS.card(m.id))}
                    data-testid={`admin-model-row-${m.id}`}
                  >
                    <td className="p-2 font-medium">{m.name}</td>
                    <td className="p-2 text-muted-foreground">{m.faction || "—"}</td>
                    <td className="p-2 text-muted-foreground">{m.type || "—"}</td>
                    <td className="p-2 text-right tabular-nums">{m.rule_count}</td>
                    <td className="p-2 text-center">
                      {m.isLive ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
                      ) : (
                        <XCircle className="w-4 h-4 text-muted-foreground inline" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
          <div className="p-2 flex items-center justify-between border-t border-border text-xs text-muted-foreground">
            <span>Showing {offset + 1}–{Math.min(offset + LIMIT, data.total)} of {data.total.toLocaleString()}</span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                data-testid="admin-models-prev"
              >Prev</Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + LIMIT >= data.total}
                onClick={() => setOffset(offset + LIMIT)}
                data-testid="admin-models-next"
              >Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AdminDiff() {
  const { navigate } = useNav();
  const [info, setInfo] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.adminInfo().then((d) => {
      setInfo(d);
      const versions = d.snapshot.history || [];
      if (versions.length > 0) setTo(versions[0].version_id);
      if (versions.length > 1) setFrom(versions[1].version_id);
    });
  }, []);

  const runDiff = async () => {
    setLoading(true);
    setError(null);
    setDiff(null);
    try {
      const d = await api.adminDiff({ from, to });
      setDiff(d);
    } catch (e) {
      setError(e?.message || "Diff failed");
    } finally {
      setLoading(false);
    }
  };

  if (!info) return <Skeleton className="h-40 w-full" />;
  const versions = info.snapshot.history || [];
  const onlyOne = versions.length < 2;

  return (
    <div className="grid gap-3" data-testid="admin-diff-panel">
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold flex items-center gap-2 mb-3">
            <GitCompare className="w-4 h-4 text-primary" /> Compare snapshots
          </h3>
          {onlyOne ? (
            <p className="text-sm text-muted-foreground italic">
              Only one snapshot exists. Trigger another sync to enable diffing.
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-muted-foreground">From</label>
              <select
                className="bg-background border border-border rounded px-3 py-1.5 text-sm"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                data-testid="diff-from-select"
              >
                {versions.map((v) => (
                  <option key={v.version_id} value={v.version_id}>{v.version_id}</option>
                ))}
              </select>
              <label className="text-xs text-muted-foreground">to</label>
              <select
                className="bg-background border border-border rounded px-3 py-1.5 text-sm"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                data-testid="diff-to-select"
              >
                {versions.map((v) => (
                  <option key={v.version_id} value={v.version_id}>{v.version_id}</option>
                ))}
              </select>
              <Button onClick={runDiff} disabled={loading || !from || !to} className="gap-2" data-testid="diff-run-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />}
                Compute diff
              </Button>
            </div>
          )}
          {error && (
            <p className="text-sm text-rose-400 mt-3">{error}</p>
          )}
        </CardContent>
      </Card>

      {diff && (
        <Card data-testid="diff-result">
          <CardContent className="p-4 space-y-4">
            {diff.message && (
              <p className="text-sm text-muted-foreground italic">{diff.message}</p>
            )}
            <DiffSection title="Cards" data={diff.card_level} onOpen={(id) => navigate(VIEWS.card(id))} />
            {Object.entries(diff.rule_level || {}).map(([sect, d]) => (
              <DiffSection
                key={sect}
                title={`Rules — ${sect.replace("Abilities", " abilities")}`}
                data={d}
                onOpen={(id) => navigate(VIEWS.rule(id))}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DiffSection({ title, data, onOpen }) {
  if (!data) return null;
  const empty = data.added_count + data.removed_count + data.modified_count === 0;
  return (
    <div data-testid={`diff-section-${title}`}>
      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
        {title}
        <Badge variant="outline" className="text-emerald-400 border-emerald-700/60">+{data.added_count}</Badge>
        <Badge variant="outline" className="text-amber-400 border-amber-700/60">~{data.modified_count}</Badge>
        <Badge variant="outline" className="text-rose-400 border-rose-700/60">−{data.removed_count}</Badge>
      </h4>
      {empty ? (
        <p className="text-xs text-muted-foreground italic">No changes.</p>
      ) : (
        <div className="grid md:grid-cols-3 gap-2">
          <DiffList title="Added" items={data.added} color="text-emerald-400" onOpen={onOpen} />
          <DiffList title="Modified" items={data.modified} color="text-amber-400" onOpen={onOpen} showChanges />
          <DiffList title="Removed" items={data.removed} color="text-rose-400" onOpen={onOpen} />
        </div>
      )}
    </div>
  );
}

function DiffList({ title, items, color, onOpen, showChanges = false }) {
  if (!items || items.length === 0) {
    return (
      <div className="text-[11px] text-muted-foreground p-2 rounded border border-border/40">
        <p className={`uppercase tracking-wide ${color}`}>{title}</p>
        <p className="italic">none</p>
      </div>
    );
  }
  return (
    <div className="p-2 rounded border border-border/60">
      <p className={`text-[11px] uppercase tracking-wide mb-1 ${color}`}>{title} · {items.length}</p>
      <ScrollArea className="max-h-48">
        <div className="space-y-1 pr-2">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              className="block w-full text-left text-xs px-2 py-1 rounded hover:bg-card/80 border border-transparent hover:border-primary/40"
              onClick={() => onOpen && onOpen(it.id)}
            >
              <span className="font-medium">{it.name || it.id?.slice(0, 8)}</span>
              {showChanges && it.changes && (
                <div className="text-[10px] text-muted-foreground mt-1 space-y-0.5">
                  {Object.keys(it.changes).slice(0, 3).map((f) => (
                    <div key={f}><code className="text-cyan-400">{f}</code> changed</div>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

function PdfStatusCard({ pdf, title, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-cyan-400" />
          <h3 className="font-semibold">{title}</h3>
          {pdf?.present ? (
            <Badge variant="outline" className="ml-auto text-emerald-400 border-emerald-700/60" data-testid={`${testId}-status`}>present</Badge>
          ) : (
            <Badge variant="destructive" className="ml-auto" data-testid={`${testId}-status`}>missing</Badge>
          )}
        </div>
        {pdf?.present ? (
          <dl className="text-sm space-y-1">
            <Row label="File" value={pdf.filename} />
            <Row label="Size" value={fmtBytes(pdf.size_bytes)} />
            <Row label="Modified" value={fmtDate(pdf.modified_at)} />
            <Row label="Pages" value={pdf.page_count} />
            <Row label="Cached page images" value={`${pdf.cached_page_images} / ${pdf.page_count}`} />
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground italic">No PDF found.</p>
        )}
      </CardContent>
    </Card>
  );
}

function SyncRunner({ onComplete }) {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  // Mutable cancellation token shared across both effects.
  const cancelRef = React.useRef({ cancelled: false });

  // Resume polling any in-flight sync after a page reload + register cleanup.
  useEffect(() => {
    const token = cancelRef.current;
    token.cancelled = false;
    (async () => {
      try {
        const a = await fetch(`${BASE}/api/admin/sync/active`, { credentials: "include" }).then((r) => r.json());
        if (token.cancelled) return;
        if (a.active) {
          pollLoop(a.job_id);
        }
      } catch (e) {
        console.warn("Could not check for an active sync", e);
      }
    })();
    return () => { token.cancelled = true; };
    // Mount-only: resume an in-flight sync once; pollLoop reads state via cancelRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollLoop = async (jobId) => {
    const token = cancelRef.current;
    let lastVersion = -1;
    while (!token.cancelled) {
      await new Promise((r) => setTimeout(r, 900));
      if (token.cancelled) return;
      let s;
      try {
        s = await fetch(`${BASE}/api/admin/sync/status/${jobId}`, { credentials: "include" }).then((r) => r.json());
      } catch (_e) {
        continue;
      }
      if (token.cancelled) return;
      if (s.version !== lastVersion) {
        lastVersion = s.version;
        setJob(s);
      }
      if (s.status === "complete") {
        if (onComplete) onComplete();
        return;
      }
      if (s.status === "error") return;
    }
  };

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const r = await fetch(`${BASE}/api/admin/sync/start`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const { job_id } = await r.json();
      setJob({ id: job_id, status: "running", current_step: 0, total_steps: 8, current_label: "Queued", message: "Queued…", log: [], version: 0 });
      pollLoop(job_id);
    } catch (e) {
      setError(e?.message || "Failed to start sync");
    } finally {
      setStarting(false);
    }
  };

  const isRunning = job && job.status === "running";
  const pct = job ? Math.round((job.current_step / job.total_steps) * 100) : 0;

  return (
    <Card data-testid="sync-runner-card" className={isRunning ? "border-primary/50" : ""}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <RefreshCw className={`w-4 h-4 text-primary ${isRunning ? "animate-spin" : ""}`} />
          <h3 className="font-semibold">Force resync</h3>
          <span className="text-xs text-muted-foreground">
            Pulls fresh card data from PlayFab → S3 → decrypts → saves a new snapshot. Usually ~60-90s.
          </span>
          <Button
            onClick={start}
            disabled={starting || isRunning}
            className="ml-auto gap-2"
            data-testid="sync-start-btn"
          >
            {starting || isRunning ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Syncing…</>
            ) : (
              <><RefreshCw className="w-4 h-4" /> Start sync</>
            )}
          </Button>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-2 mb-2 rounded border border-rose-700/60 bg-rose-950/15 text-sm text-rose-300">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {job && (
          <div className="space-y-2" data-testid="sync-progress">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                Step <span data-testid="sync-current-step">{job.current_step}</span> / {job.total_steps}
                {" — "}
                <span className="text-foreground font-medium" data-testid="sync-current-label">{job.current_label}</span>
              </span>
              <span
                className={`tabular-nums ${
                  job.status === "complete" ? "text-emerald-400" :
                  job.status === "error" ? "text-rose-400" :
                  "text-muted-foreground"
                }`}
                data-testid="sync-status"
              >
                {job.status} · {pct}%
              </span>
            </div>
            {/* Progress bar */}
            <div className="h-2 w-full rounded bg-card border border-border overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  job.status === "error" ? "bg-rose-500" :
                  job.status === "complete" ? "bg-emerald-500" :
                  "bg-primary"
                }`}
                style={{ width: `${pct}%` }}
                data-testid="sync-progress-bar"
              />
            </div>
            <p className="text-xs text-muted-foreground italic" data-testid="sync-message">{job.message}</p>
            {job.status === "complete" && job.result && (
              <p className="text-sm text-emerald-400" data-testid="sync-complete">
                <CheckCircle2 className="w-4 h-4 inline mr-1" />
                New snapshot {job.result.version_id} saved with {job.result.bundle_count} bundles.
              </p>
            )}
            {job.status === "error" && (
              <p className="text-sm text-rose-300" data-testid="sync-error">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                {job.error}
              </p>
            )}
            {job.log && job.log.length > 0 && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground" data-testid="sync-log-toggle">Show log ({job.log.length})</summary>
                <ScrollArea className="max-h-40 mt-2">
                  <ul className="space-y-0.5 font-mono text-[11px] pl-2" data-testid="sync-log">
                    {job.log.map((l, i) => <li key={i}>· {l.msg}</li>)}
                  </ul>
                </ScrollArea>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function RulebookUploader({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = React.useRef(null);

  const submit = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Please pick a .pdf file.");
      return;
    }
    setUploading(true);
    setError(null);
    setResult(null);
    setProgress(0);
    try {
      const r = await api.uploadRulebook(file, (ev) => {
        if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
      });
      setResult(r);
      if (onComplete) onComplete();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    submit(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    submit(file);
  };

  return (
    <Card data-testid="rulebook-uploader-card" className={uploading ? "border-primary/50" : ""}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <BookOpen className="w-4 h-4 text-cyan-400" />
          <h3 className="font-semibold">Replace rulebook PDF</h3>
          <span className="text-xs text-muted-foreground">
            Upload a new edition. Current PDF is backed up; page images & section cache are rebuilt.
          </span>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={onPick}
            disabled={uploading}
            className="hidden"
            data-testid="rulebook-file-input"
          />
          <Button
            className="ml-auto gap-2"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="rulebook-pick-btn"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Uploading… {progress}%</>
            ) : (
              <><Upload className="w-4 h-4" /> Choose PDF</>
            )}
          </Button>
        </div>

        <div
          className="rounded-md border-2 border-dashed border-border p-4 text-center text-xs text-muted-foreground hover:border-primary/40 transition cursor-pointer"
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          data-testid="rulebook-drop-zone"
        >
          {uploading
            ? <p>Uploading & re-extracting sections…</p>
            : <p>Click or drop a .pdf here. Max 100 MB.</p>}
        </div>

        {uploading && (
          <div className="mt-3 space-y-1" data-testid="rulebook-upload-progress">
            <div className="h-2 w-full rounded bg-card border border-border overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-200"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-[11px] text-muted-foreground tabular-nums">{progress}% uploaded</p>
          </div>
        )}

        {error && (
          <div className="mt-3 flex items-start gap-2 p-2 rounded border border-rose-700/60 bg-rose-950/15 text-sm text-rose-300" data-testid="rulebook-upload-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="mt-3 p-2 rounded border border-emerald-700/40 bg-emerald-950/15 text-sm" data-testid="rulebook-upload-result">
            <p className="text-emerald-400 font-medium flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Uploaded {result.uploaded} ({(result.size_bytes / 1024 / 1024).toFixed(1)} MB)
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Backup: <code>{result.backup || "—"}</code>
            </p>
            <p className="text-xs text-muted-foreground">
              Sections rebuilt: <strong>{result.rescan?.sections}</strong>
              {" — "}
              auto-remapped: <strong>{result.rescan?.remapped}</strong>
              {" of "}{result.rescan?.total_pdf_pages} pages
            </p>
            {result.rescan?.warnings?.length > 0 && (
              <details className="text-[11px] text-amber-300 mt-1">
                <summary className="cursor-pointer hover:text-amber-200">
                  {result.rescan.warnings.length} sections fell back to hardcoded ranges
                </summary>
                <ul className="pl-2 mt-1 space-y-0.5">
                  {result.rescan.warnings.map((w, i) => <li key={i}>· {w}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function SteamrollerUploader({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = React.useRef(null);

  const submit = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Please pick a .pdf file.");
      return;
    }
    setUploading(true);
    setError(null);
    setResult(null);
    setProgress(0);
    try {
      const r = await api.uploadSteamroller(file, (ev) => {
        if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
      });
      setResult(r);
      if (onComplete) onComplete();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => submit(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    if (uploading) return;
    submit(e.dataTransfer.files?.[0]);
  };

  return (
    <Card data-testid="steamroller-uploader-card" className={uploading ? "border-amber-700/60" : ""}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <Trophy className="w-4 h-4 text-amber-400" />
          <h3 className="font-semibold">Replace Steamroller PDF</h3>
          <span className="text-xs text-muted-foreground">
            Upload the new yearly organized-play rules. Current PDF backed up; sections + page images rebuilt.
          </span>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={onPick}
            disabled={uploading}
            className="hidden"
            data-testid="steamroller-file-input"
          />
          <Button
            className="ml-auto gap-2"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="steamroller-pick-btn"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Uploading… {progress}%</>
            ) : (
              <><Upload className="w-4 h-4" /> Choose PDF</>
            )}
          </Button>
        </div>

        <div
          className="rounded-md border-2 border-dashed border-border p-4 text-center text-xs text-muted-foreground hover:border-amber-700/40 transition cursor-pointer"
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          data-testid="steamroller-drop-zone"
        >
          {uploading
            ? <p>Uploading & re-extracting Steamroller sections…</p>
            : <p>Click or drop a Steamroller .pdf here. Max 100 MB.</p>}
        </div>

        {uploading && (
          <div className="mt-3 space-y-1" data-testid="steamroller-upload-progress">
            <div className="h-2 w-full rounded bg-card border border-border overflow-hidden">
              <div className="h-full bg-amber-500 transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-[11px] text-muted-foreground tabular-nums">{progress}% uploaded</p>
          </div>
        )}

        {error && (
          <div className="mt-3 flex items-start gap-2 p-2 rounded border border-rose-700/60 bg-rose-950/15 text-sm text-rose-300" data-testid="steamroller-upload-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="mt-3 p-2 rounded border border-emerald-700/40 bg-emerald-950/15 text-sm" data-testid="steamroller-upload-result">
            <p className="text-emerald-400 font-medium flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Uploaded {result.uploaded} ({(result.size_bytes / 1024 / 1024).toFixed(1)} MB)
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Backup: <code>{result.backup || "—"}</code>
            </p>
            <p className="text-xs text-muted-foreground">
              Sections rebuilt: <strong>{result.rescan?.sections}</strong>
              {" over "}{result.rescan?.total_pdf_pages} pages
            </p>
            {result.rescan?.warnings?.length > 0 && (
              <details className="text-[11px] text-amber-300 mt-1">
                <summary className="cursor-pointer hover:text-amber-200">
                  {result.rescan.warnings.length} warnings
                </summary>
                <ul className="pl-2 mt-1 space-y-0.5">
                  {result.rescan.warnings.map((w, i) => <li key={i}>· {w}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

